import { useParams } from 'react-router'

import { taskRoute } from '@/app/routes'
import { useDashboard, useRoadmap, useTasks } from '@/shared/api/queries'
import type { Evaluation } from '@/shared/api/queries'
import { useDeadlineText } from '@/shared/format'
import { useTranslate } from '@/shared/i18n'
import type { TranslationKey } from '@/shared/i18n'
import {
  areaIcon,
  areaLabel,
  byUrgency,
  evaluationsOf,
  isArea,
  tasksOfArea,
  urgencyOf,
} from '@/shared/model'
import {
  AttentionCard,
  Badge,
  Card,
  Progress,
  Screen,
  Section,
  Skeleton,
  Stack,
  StateBlock,
  Text,
} from '@/shared/ui'

function statusLabel(status: string): TranslationKey {
  return `roadmap.status.${status === 'satisfied' || status === 'overdue' ? status : 'pending'}` as TranslationKey
}

function Requirement({ item }: { item: Evaluation }) {
  const t = useTranslate()
  const deadline = useDeadlineText()

  return (
    <li>
      <Card flat className={`tone--${item.status === 'satisfied' ? 'done' : urgencyOf(item)}`}>
        <Stack direction="row" justify="between" gap="sm">
          <Text as="span" role="body-strong">
            {item.title}
          </Text>
          <Badge tone>{t(statusLabel(item.status))}</Badge>
        </Stack>
        {item.description ? (
          <Text role="caption" tone="muted">
            {item.description}
          </Text>
        ) : null}
        <Text role="caption" tone="muted">
          {deadline(item.deadline) ?? t('roadmap.no_deadline')} ·{' '}
          {t('roadmap.source', { source: item.source })}
        </Text>
      </Card>
    </li>
  )
}

export function AreaPage() {
  const t = useTranslate()
  const deadline = useDeadlineText()
  const { category } = useParams()

  const tasks = useTasks()
  const roadmap = useRoadmap()
  const dashboard = useDashboard()

  if (!category || !isArea(category)) {
    return <Screen title={t('error.generic.title')} />
  }

  const evaluations = evaluationsOf(roadmap.data)
  const open = tasksOfArea(
    (tasks.data ?? []).filter((task) => task.status === 'open'),
    evaluations,
    category,
  ).sort((a, b) => byUrgency(urgencyOf(a), urgencyOf(b)))

  const requirements = evaluations.filter((item) => item.category === category)
  const status = dashboard.data?.status.find((section) => section.category === category)

  return (
    <Stack gap="section" className="page--detail">
      <Stack as="header" gap="xs">
        <Text role="eyebrow" tone="muted">
          {areaIcon(category)}
        </Text>
        <Text as="h1" role="heading-1">
          {t(areaLabel(category))}
        </Text>
      </Stack>

      {status ? (
        <ul className="bento">
          <li {...(status.next_deadline ? {} : { className: 'bento__full' })}>
            <Card>
              <Text role="eyebrow" tone="muted">
                {t('area.requirements')}
              </Text>
              <Stack gap="xs">
                <Text role="heading-2">{status.done}</Text>
                <Progress
                  value={status.done}
                  max={status.total}
                  label={t(areaLabel(category))}
                  caption={t('area.progress', { done: status.done, total: status.total })}
                />
              </Stack>
            </Card>
          </li>

          {status.next_deadline ? (
            <li>
              <Card
                className={`tone--${urgencyOf({ deadline: status.next_deadline, hard_deadline: false })}`}
              >
                <Text role="eyebrow" tone="muted">
                  {t('timeline.title')}
                </Text>
                <Text role="title" tone="tone">
                  {deadline(status.next_deadline)}
                </Text>
              </Card>
            </li>
          ) : null}
        </ul>
      ) : null}

      <StateBlock
        pending={tasks.isPending || roadmap.isPending}
        error={tasks.error ?? roadmap.error}
        onRetry={() => void (tasks.error ? tasks.refetch() : roadmap.refetch())}
        skeleton={<Skeleton variant="card" rows={3} />}
        {...(open.length === 0 && requirements.length === 0
          ? { empty: { title: t('area.done.title'), body: t('area.done.body') } }
          : {})}
      >
        <Stack gap="section">
          {open.length > 0 ? (
            <Section title={t('area.todo.title')}>
              <Stack as="ul" gap="md">
                {open.map((task) => (
                  <AttentionCard
                    key={task.id}
                    to={taskRoute(task.id)}
                    icon={areaIcon(category)}
                    area={t(areaLabel(category))}
                    title={task.title}
                    when={deadline(task.deadline) ?? t('tasks.no_deadline')}
                    action={t('home.attention.action')}
                    urgency={urgencyOf(task)}
                  />
                ))}
              </Stack>
            </Section>
          ) : null}

          {requirements.length > 0 ? (
            <Section title={t('area.requirements')}>
              <Stack as="ul" gap="sm">
                {requirements.map((item) => (
                  <Requirement key={`${item.key}-${item.version}`} item={item} />
                ))}
              </Stack>
            </Section>
          ) : null}
        </Stack>
      </StateBlock>
    </Stack>
  )
}
